import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { SafeImg } from "./SafeImg";
import { Lightbox } from "./Lightbox";

const Arrow = ({ dir, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-label={dir === "prev" ? "Previous photo" : "Next photo"}
    className={`absolute top-1/2 ${dir === "prev" ? "left-3" : "right-3"} grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink opacity-90 shadow transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100`}>
    {dir === "prev" ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
  </button>
);

export const RoomGallery = ({ room, className }) => {
  const pics = (room.images?.length ? room.images : [room.image]).filter(Boolean);
  const [ref, api] = useEmblaCarousel({ loop: pics.length > 1 });
  const [at, setAt] = useState(0);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  useEffect(() => {
    if (!api) {
      return;
    }
    const on = () => setAt(api.selectedScrollSnap());
    api.on("select", on);
    return () => { api.off("select", on); };
  }, [api]);
  const box = open !== null && <Lightbox pics={pics} start={open} title={room.name} onClose={close} />;
  if (pics.length < 2) {
    return (
      <>
        <button type="button" className={`block ${className}`} disabled={!pics.length} onClick={() => setOpen(0)} data-testid={`room-photo-open-${room.id}`} aria-label={`View ${room.name} photo`}>
          <SafeImg src={pics[0]} alt={room.name} kind="room" testid={`room-image-placeholder-${room.id}`} className="h-full w-full object-cover" />
        </button>
        {box}
      </>
    );
  }
  return (
    <div className={`group relative overflow-hidden ${className}`} data-testid={`room-gallery-${room.id}`}>
      <div ref={ref} className="h-full overflow-hidden">
        <div className="flex h-full">
          {pics.map((p, i) => (
            <div key={p} className="h-full min-w-0 flex-[0_0_100%] cursor-zoom-in" onClick={() => setOpen(i)} data-testid={`room-photo-open-${room.id}-${i}`}>
              <SafeImg src={p} alt={`${room.name} photo ${i + 1}`} kind="room" compact className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      </div>
      <Arrow dir="prev" onClick={() => api?.scrollPrev()} testid={`room-gallery-prev-${room.id}`} />
      <Arrow dir="next" onClick={() => api?.scrollNext()} testid={`room-gallery-next-${room.id}`} />
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid={`room-gallery-dots-${room.id}`}>
        {pics.map((p, i) => (
          <button key={p} type="button" aria-label={`Photo ${i + 1}`} onClick={() => api?.scrollTo(i)}
            className={`h-1.5 rounded-full bg-white transition-[width,opacity] ${i === at ? "w-5 opacity-100" : "w-1.5 opacity-60"}`} />
        ))}
      </div>
      <span className="absolute right-3 top-3 rounded-full bg-shade/60 px-2 py-0.5 font-mono text-[11px] text-white" data-testid={`room-gallery-count-${room.id}`}>{at + 1}/{pics.length}</span>
      {box}
    </div>
  );
};
